// libraries
import {
  Button,
  ButtonDesigns,
  ButtonTypes,
  ErrorList,
  getFontCss,
  styleConstants,
  TextInputField,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import { postCognitoChangePassword } from 'src/app/ChangePassword/redux/changePassword.actions'
import {
  selectChangePasswordErrorDetailMapFromState,
  selectChangePasswordLoadingFromState,
  selectChangePasswordTopLevelErrorFromState,
} from 'src/app/ChangePassword/redux/changePassword.selectors'
import PasswordInputField from 'src/components/PasswordInputField/PasswordInputField'
import { useBackgroundDispatch, useFormFields } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import {
  stringHasSpace,
  stringIsEmpty,
} from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

interface ChangePasswordFormProps {
  email: string
}

export type ChangePasswordFormFlatFields = {
  currentPassword: string
  newPassword: string
}

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useBackgroundDispatch()

  const topLevelError = useSelector(selectChangePasswordTopLevelErrorFromState)
  const errorDetailMap = useSelector(
    selectChangePasswordErrorDetailMapFromState,
  )
  const loading = useSelector(selectChangePasswordLoadingFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    const passedFlatFieldsValidation = flatFieldsSetErrors()

    if (passedFlatFieldsValidation) {
      dispatch(
        postCognitoChangePassword({
          oldPassword: flatFields.currentPassword,
          newPassword: flatFields.newPassword,
        }),
      )
    }
  }

  const {
    fields: flatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
  } = useFormFields<ChangePasswordFormFlatFields>(
    {
      currentPassword: '',
      newPassword: '',
    },
    () => ({
      currentPassword: [stringIsEmpty()],
      newPassword: [stringIsEmpty(), stringHasSpace],
    }),
    errorDetailMap,
  )

  return (
    <form
      css={css`
        display: flex;
        flex-direction: column;
      `}
      onSubmit={onSubmit}
    >
      <div
        css={css`
          ${getFontCss(
            32,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          margin-bottom: 24px;
        `}
      >
        {translate('Change your password')}
      </div>
      <div
        css={css`
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
          margin-bottom: 24px;
        `}
      >
        {translate(
          "Choose a strong password and don't reuse it for other accounts.",
        )}
      </div>
      {/* hidden email field for accessibility reasons */}
      <TextInputField
        css={css`
          display: none;
        `}
        value={props.email}
        placeholder={''}
        onChange={(): null => {
          return null
        }}
        type={'email'}
        autoComplete={'email'}
        errors={errorDetailMap?.['email']}
        inputAriaLabel={'email address'}
      />
      <div
        css={css`
          margin-bottom: 40px;
        `}
      >
        <div
          css={css`
            margin-bottom: 30px;
          `}
        >
          <PasswordInputField
            value={flatFields.currentPassword}
            onChange={(val: string): void =>
              onFlatFieldChange('currentPassword', val)
            }
            errors={flatFieldsErrors['currentPassword']}
            fieldLabel={translate('Current password')}
            placeHolder={translateSingleString(
              locale,
              'Enter current password',
            )}
          />
        </div>
        <PasswordInputField
          value={flatFields.newPassword}
          onChange={(val: string): void =>
            onFlatFieldChange('newPassword', val)
          }
          errors={flatFieldsErrors['newPassword']}
          fieldLabel={translate('New password')}
          placeHolder={translateSingleString(locale, 'Enter new password')}
          isNewPassword
        />
      </div>
      <Button
        type={ButtonTypes.submit}
        design={ButtonDesigns.designCta}
        text={translate('Change Password')}
        loading={loading}
        // TODO: add validation for disabled state
        disabled={loading}
        big
      />
      {topLevelError !== undefined && <ErrorList errors={[topLevelError]} />}
    </form>
  )
}

export default ChangePasswordForm
